// No direct helper exists for parsing stream id in this project; copy broadcaster's logic for now
import { LOW_CONTENT_STREAM_PART_PARTITION, Settings, } from '@streamr/tv-engine';
import { executeSoon } from '@streamr/tv-utils';
import { binaryToHex, hexToBinary, toUserId, toUserIdRaw, } from '@streamr/utils';
import { Env } from '../../Env';
import { generateKeysAndUserId } from '../../engine/helpers/generateKeysAndUserId';
import { KeyStore } from '../../engine/helpers/KeyStore';
import { StreamrAppApi, } from '../../engine/StreamrAppApi';
import { OgrafManager } from '../../engine/streamrapp/ograf/OgrafManager';
import { UserManager } from '../../engine/streamrapp/UserManager';
import { UserStore } from '../../engine/streamrapp/UserStore';
import { canonicalUserId } from '../../utils/canonicalUserId';
import { formFullStreamId, formShortStreamId, generateStreamId, } from '../../utils/streamId';
import { formBroadcastPath, formBroadcastUrl, parseBroadcastFromUrl, } from '../../utils/url';
import { BroadcastStateService } from '../broadcaststate/BroadcastStateService';
import { DestinationsService } from '../destinations/DestinationsService';
import { LobbyService } from '../lobby/LobbyService';
import { MediaService } from '../media/MediaService';
import { OgrafService } from '../ograf/OgrafService';
import { PluginApiService } from '../pluginapi/PluginApiService';
import { PurchaseService } from '../purchase/PurchaseService';
import { RecordingService } from '../recording/RecordingService';
import { SceneService } from '../scene/SceneService';
import { generateSessionId, } from '../schedule/ScheduledSession';
import { ScheduleService } from '../schedule/ScheduleService';
import { UserService } from '../user/UserService';
import { AcceptedParticipantType, UserRole, UserServiceOutput, } from '../user/UserServiceOutput';
import { VideoPropertiesService } from '../videoProperties/VideoPropertiesService';
import { ViewerCountService } from '../viewercount/ViewerCountService';
import { StreamrAppServiceOutput, } from './StreamrAppServiceOutput';
import { ViewerQualityStore } from './ViewerQualityStore';
/** recorder heartbeats arrive every few seconds; after this silence it is gone */
export class StreamrAppService {
    static instance;
    static getInstance() {
        if (!StreamrAppService.instance) {
            StreamrAppService.instance = new StreamrAppService();
        }
        return StreamrAppService.instance;
    }
    static getNotificationInterface() {
        return StreamrAppService.getInstance();
    }
    static getOutput() {
        return StreamrAppServiceOutput.getReactiveOutput();
    }
    // --- Singleton end
    roleComposites(role) {
        return (role === UserRole.SPEAKER ||
            role === UserRole.COHOST ||
            role === UserRole.CAMERA);
    }
    roleAddsLocalTracks(role) {
        return (role === UserRole.SPEAKER ||
            role === UserRole.COHOST ||
            role === UserRole.CAMERA ||
            role === UserRole.GUEST);
    }
    roleUsesSpeechDetection(role) {
        return (role === UserRole.SPEAKER ||
            role === UserRole.COHOST ||
            role === UserRole.CAMERA);
    }
    isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    // Safari/WebKit. `navigator.vendor` survives UA overrides (Playwright
    // forces a Chrome UA on WebKit launches) and reliably discriminates
    // WebKit from Chromium / Gecko.
    isWebKit = /Apple Computer/.test(navigator.vendor ?? '');
    /**
     * What the worker may show the display scene on for this role. On
     * WebKit the renderer hands a single shown video to a <video> element
     * (every canvas costs CPU there) and paints the canvas for anything
     * else; iPhone viewers start direct so their first frame is not
     * composed. Elsewhere the canvas, always.
     */
    applyDisplayOutputPolicy(role) {
        const mediaManager = this.streamrAppApi?.getMediaManager();
        if (!mediaManager)
            return;
        const isViewingRole = role === UserRole.VIEWER || role === UserRole.GUEST;
        mediaManager
            .setDirectOutputPolicy(this.isWebKit
            ? {
                preferDirect: 'auto',
                initialMode: this.isIos && isViewingRole
                    ? 'direct'
                    : 'composed',
            }
            : { preferDirect: 'never' })
            .catch(console.error);
    }
    onDisplayOutputChanged(event) {
        const previous = this.output.getDirectVideoTrack();
        if (event.mode === 'direct') {
            this.output.setDirectVideoTrack(event.track);
        }
        else {
            this.output.setDirectVideoTrack(null);
            if (event.canvasGeneration !== this.output.getCanvasGeneration()) {
                this.output.setCanvasGeneration(event.canvasGeneration);
            }
        }
        if (previous && previous !== this.output.getDirectVideoTrack()) {
            previous.stop();
        }
    }
    getVideoProfileForRole(role) {
        const isCall = UserServiceOutput.getAcceptedParticipantType() ===
            AcceptedParticipantType.SPEAKERS;
        let profile;
        switch (role) {
            case UserRole.COHOST:
                profile = isCall
                    ? {
                        width: Settings.cohostCallVideoWidth,
                        height: Settings.cohostCallVideoHeight,
                        bitrate: Settings.cohostCallVideoBitrate,
                    }
                    : {
                        width: Settings.cohostVideoWidth,
                        height: Settings.cohostVideoHeight,
                        bitrate: Settings.cohostVideoBitrate,
                    };
                break;
            case UserRole.SPEAKER:
                profile = {
                    width: Settings.speakerVideoWidth,
                    height: Settings.speakerVideoHeight,
                    bitrate: Settings.speakerVideoBitrate,
                };
                break;
            case UserRole.GUEST:
                profile = {
                    width: Settings.guestVideoWidth,
                    height: Settings.guestVideoHeight,
                    bitrate: Settings.guestVideoBitrate,
                };
                break;
            case UserRole.CAMERA:
                profile = {
                    width: Settings.cameraVideoWidth,
                    height: Settings.cameraVideoHeight,
                    bitrate: Settings.cameraVideoBitrate,
                };
                break;
            default:
                profile = {
                    width: Settings.speakerVideoWidth,
                    height: Settings.speakerVideoHeight,
                    bitrate: Settings.speakerVideoBitrate,
                };
                break;
        }
        return profile;
    }
    mediaService;
    streamrAppApi;
    output;
    broadcastActivated = false;
    currentStreamId;
    currentBroadcast;
    displayConfigVersion = 0;
    liveConfigVersion = 0;
    thumbnailCaptureTimer;
    transferredThumbnailCanvasIds = new Set();
    thumbnailTransferRetries = {};
    thumbnailCanvasMap = new Map();
    thumbnailSyncInFlight = false;
    activeThumbnailParticipantIds = [];
    pendingCanvas;
    canvasReadyResolve;
    canvasReadyPromise;
    latestVideoTracks = [];
    latestAudioTracks = [];
    // viewer side of the low-bandwidth ladder (see syncViewerBroadcasterPlaceholder)
    viewerSawBroadcasterVideo = false;
    userManager;
    renderingReadyResolve;
    renderingReadyPromise;
    broadcastMetadataIntervalId;
    pendingCameraOneToOneStreamUrl;
    /** id of the shared-tab audio track currently published as an extra local track */
    publishedDisplayAudioTrackId;
    lowEndModeEnabled = false;
    engineStartedResolve;
    engineStartedPromise;
    constructor() {
        this.output = new StreamrAppServiceOutput();
        this.mediaService = MediaService.getInstance();
        this.mediaService.on('mediaStreamChanged', () => {
            void this.onMediaStreamChanged();
        });
        this.mediaService.on('additionalTrackAdded', this.onAdditionalTrackAdded);
        this.mediaService.on('additionalTrackRemoved', this.onAdditionalTrackRemoved);
        const sceneService = SceneService.getInstance();
        sceneService.on('displaySceneChanged', this.onDisplaySceneChanged);
        sceneService.on('liveSceneChanged', this.onLiveSceneChanged);
    }
    async init() {
        // No initialization needed
    }
    notifyCanvasMounted(canvas) {
        this.pendingCanvas = canvas;
        if (this.streamrAppApi) {
            this.streamrAppApi
                .getMediaManager()
                .setDisplayCanvas(canvas)
                .then(() => {
                this.canvasReadyResolve?.();
                if (this.broadcastActivated) {
                    void this.onMediaStreamChanged();
                }
            })
                .catch((err) => {
                console.warn('[StreamrAppService] setDisplayCanvas failed in notifyCanvasMounted', err);
            });
        }
    }
    notifyLiveSceneThumbnailCanvasMounted(canvas) {
        this.streamrAppApi
            ?.getMediaManager()
            .setLiveSceneThumbnailCanvasMounted(canvas)
            .catch(() => undefined);
    }
    notifyCanvasClicked(x, y) {
        this.streamrAppApi?.handleCanvasClick(x, y).catch(console.error);
    }
    onDisplaySceneChanged = (scene) => {
        if (!this.broadcastActivated)
            return;
        this.buildAndPushDisplayConfig(scene);
    };
    onLiveSceneChanged = (scene) => {
        if (!this.broadcastActivated)
            return;
        this.buildAndPushLiveConfig(scene);
    };
    static THUMBNAIL_CAPTURE_DELAY_MS = 500;
    static THUMBNAIL_WIDTH = 320;
    static THUMBNAIL_HEIGHT = 180;
    static MAX_THUMBNAIL_TRANSFER_RETRIES = 3;
    buildAndPushDisplayConfig(scene) {
        if (!scene)
            return;
        const version = ++this.displayConfigVersion;
        void SceneService.getInstance()
            .resolveScene(scene)
            .then((config) => {
            if (this.displayConfigVersion !== version)
                return;
            this.streamrAppApi
                ?.getMediaManager()
                .setDisplayScene(config)
                .catch(console.error);
            this.scheduleThumbnailCapture(scene.id);
        });
    }
    scheduleThumbnailCapture(sceneId) {
        if (this.thumbnailCaptureTimer) {
            clearTimeout(this.thumbnailCaptureTimer);
        }
        this.thumbnailCaptureTimer = setTimeout(() => {
            this.thumbnailCaptureTimer = undefined;
            void this.captureThumbnail(sceneId);
        }, StreamrAppService.THUMBNAIL_CAPTURE_DELAY_MS);
    }
    async captureThumbnail(sceneId) {
        const manager = this.streamrAppApi?.getMediaManager();
        if (!manager)
            return;
        try {
            const dataUrl = await manager.captureDisplayFrame(StreamrAppService.THUMBNAIL_WIDTH, StreamrAppService.THUMBNAIL_HEIGHT);
            if (dataUrl) {
                SceneService.getNotificationInterface().notifySetSceneThumbnail(sceneId, dataUrl);
            }
        }
        catch (e) {
            console.warn('Failed to capture scene thumbnail', e);
        }
    }
    buildAndPushLiveConfig(scene) {
        const effectiveScene = scene ?? SceneService.getInstance().getDisplayScene();
        if (!effectiveScene)
            return;
        const version = ++this.liveConfigVersion;
        void SceneService.getInstance()
            .resolveScene(effectiveScene)
            .then((config) => {
            if (this.liveConfigVersion !== version)
                return;
            this.streamrAppApi
                ?.getMediaManager()
                .setLiveScene(config)
                .catch(console.error);
        });
    }
    static async setThumbnailRenderingConfig(entries) {
        const manager = StreamrAppService.getInstance().streamrAppApi?.getMediaManager();
        if (!manager)
            return false;
        await manager.setThumbnailRenderingConfig(entries);
        return true;
    }
    notifyThumbnailCanvasMounted(canvasId, canvas) {
        if (this.transferredThumbnailCanvasIds.has(canvasId))
            return;
        try {
            const offscreen = canvas.transferControlToOffscreen();
            this.transferredThumbnailCanvasIds.add(canvasId);
            this.thumbnailTransferRetries[canvasId] = 0;
            this.thumbnailCanvasMap.set(canvasId, offscreen);
            this.syncThumbnailConfig();
        }
        catch (err) {
            const retries = this.thumbnailTransferRetries[canvasId] ?? 0;
            if (retries < StreamrAppService.MAX_THUMBNAIL_TRANSFER_RETRIES) {
                this.thumbnailTransferRetries[canvasId] = retries + 1;
                this.output.rekeyThumbnailCanvas(canvasId);
            }
            else {
                console.warn(`[THUMB] transferControlToOffscreen failed after ${StreamrAppService.MAX_THUMBNAIL_TRANSFER_RETRIES} retries for "${canvasId}"`, err);
            }
        }
    }
    notifyThumbnailCanvasUnmounted(canvasId) {
        this.transferredThumbnailCanvasIds.delete(canvasId);
        this.thumbnailCanvasMap.delete(canvasId);
        this.syncThumbnailConfig();
    }
    notifySetActiveThumbnailParticipantIds(ids) {
        const hadParticipants = this.activeThumbnailParticipantIds.length > 0;
        this.activeThumbnailParticipantIds = ids;
        if (ids.length === 0 && hadParticipants) {
            this.clearThumbnailRendering();
        }
        else {
            this.syncThumbnailConfig();
        }
    }
    syncThumbnailConfig() {
        if (this.thumbnailSyncInFlight)
            return;
        const entries = [];
        const transferableCanvasIds = [];
        for (const id of this.activeThumbnailParticipantIds) {
            if (!this.thumbnailCanvasMap.has(id))
                continue;
            const canvas = this.thumbnailCanvasMap.get(id);
            const entry = {
                canvasId: id,
                trackId: id,
            };
            if (canvas) {
                entry.canvas = canvas;
                transferableCanvasIds.push(id);
            }
            entries.push(entry);
        }
        if (entries.length === 0)
            return;
        const hasCanvases = transferableCanvasIds.length > 0;
        if (hasCanvases) {
            this.thumbnailSyncInFlight = true;
        }
        const manager = this.streamrAppApi?.getMediaManager();
        if (!manager) {
            this.thumbnailSyncInFlight = false;
            return;
        }
        manager.setThumbnailRenderingConfig(entries).then(() => {
            if (hasCanvases) {
                this.thumbnailSyncInFlight = false;
                for (const id of transferableCanvasIds) {
                    this.thumbnailCanvasMap.set(id, null);
                }
            }
        }, (err) => {
            console.error('[THUMB] setThumbnailRenderingConfig error', err);
            if (hasCanvases) {
                this.thumbnailSyncInFlight = false;
            }
        });
    }
    clearThumbnailRendering() {
        const manager = this.streamrAppApi?.getMediaManager();
        if (manager) {
            manager.setThumbnailRenderingConfig([]).catch(console.error);
        }
        this.thumbnailCanvasMap.clear();
    }
    // Notifications
    notifyInitializeBroadcastFromUrl() {
        return this.initializeBroadcastFromUrl();
    }
    /**
     * The same for components, which notify and get nothing back: the
     * broadcast shows up in BroadcastStateService's output.
     */
    notifyResolveBroadcastFromUrl() {
        this.initializeBroadcastFromUrl();
    }
    notifyPrepareRendering(activeBroadcast) {
        this.prepareRendering(activeBroadcast);
    }
    notifyActivateBroadcast(activeBroadcast) {
        this.streamrAppApi
            ?.getMediaManager()
            .unmuteAudio()
            .catch(console.error);
        this.activateBroadcast(activeBroadcast);
    }
    notifyDeactivateBroadcast() {
        this.deactivateBroadcast();
    }
    notifyDestroyRendering() {
        this.destroyRendering();
    }
    /**
     * Starts recording the broadcast of the running session. For
     * RecordingService, which owns the recording's state.
     */
    async startRecording(options) {
        if (!this.streamrAppApi) {
            throw new Error('There is no broadcast to record');
        }
        await this.streamrAppApi.startRecording(options);
    }
    async stopRecording() {
        await this.streamrAppApi?.stopRecording();
    }
    async notifyAddRtmpDestination(rtmpUrl, proxyWsUrl, onStatusChange) {
        if (!this.streamrAppApi) {
            console.warn('[StreamrAppService] notifyAddRtmpDestination: no active broadcast (streamrAppApi not initialized)');
            return undefined;
        }
        return this.streamrAppApi
            .getMediaManager()
            .addRtmpDestination(rtmpUrl, proxyWsUrl, onStatusChange);
    }
    async notifyRemoveRtmpDestination(destinationId) {
        if (!this.streamrAppApi) {
            console.warn('[StreamrAppService] notifyRemoveRtmpDestination: no active broadcast (streamrAppApi not initialized)');
            return;
        }
        await this.streamrAppApi
            .getMediaManager()
            .removeRtmpDestination(destinationId);
    }
    async notifyRemoveAllRtmpDestinations() {
        if (!this.streamrAppApi) {
            console.warn('[StreamrAppService] notifyRemoveAllRtmpDestinations: no active broadcast (streamrAppApi not initialized)');
            return;
        }
        await this.streamrAppApi.getMediaManager().removeAllRtmpDestinations();
    }
    notifySetAudioTrackMuted(trackId, muted) {
        this.streamrAppApi
            ?.getMediaManager()
            .setAudioTrackMuted(trackId, muted);
    }
    notifySetAudioTrackGain(trackId, gain) {
        this.streamrAppApi?.getMediaManager().setAudioTrackGain(trackId, gain);
    }
    handleTrackDisappearanceFallback(disappearedTracks, currentTracks, _currentTrackIds) {
        const sceneService = SceneService.getInstance();
        const displayScene = sceneService.getDisplayScene();
        if (!displayScene)
            return;
        const allAssignedSourceIds = new Set(displayScene.videoSlots
            .map((s) => s.sourceId)
            .filter((id) => !!id));
        for (const gone of disappearedTracks) {
            const slotsUsingIt = displayScene.videoSlots.filter((s) => s.sourceId === gone.trackId);
            if (slotsUsingIt.length === 0)
                continue;
            const replacementTrack = currentTracks.find((t) => t.userId === gone.userId &&
                !allAssignedSourceIds.has(t.trackId));
            if (!replacementTrack)
                continue;
            for (const slot of slotsUsingIt) {
                sceneService.notifyAssignSlotSource(displayScene.id, slot.id, replacementTrack.trackId);
                allAssignedSourceIds.add(replacementTrack.trackId);
            }
        }
    }
    ensurePlaceholderTracksForExistingUsers() {
        if (!this.userManager || !this.streamrAppApi)
            return;
        const mm = this.streamrAppApi.getMediaManager();
        for (const [userId, user] of this.userManager.getUsers()) {
            if (user.status === UserRole.SPEAKER ||
                user.status === UserRole.COHOST) {
                mm.createPlaceholderTrack(userId, user.displayName ?? userId);
            }
        }
    }
    syncPlaceholderVisibility(mm, tracks) {
        const ownRole = this.streamrAppApi?.getUserRole();
        if (!this.userManager || !this.streamrAppApi) {
            return;
        }
        if (!this.roleComposites(ownRole)) {
            this.syncViewerBroadcasterPlaceholder(mm, tracks);
            return;
        }
        const usersWithRealVideo = new Set();
        for (const t of tracks) {
            if (!t.isLocal && !t.trackId.startsWith('placeholder:')) {
                usersWithRealVideo.add(canonicalUserId(t.userId));
            }
        }
        this.ensurePlaceholderTracksForActiveRemoteUsers(mm, tracks);
        const allRemoteUserIds = new Set();
        for (const [userId, user] of this.userManager.getUsers()) {
            if (user.online) {
                allRemoteUserIds.add(canonicalUserId(userId));
            }
        }
        for (const t of tracks) {
            if (!t.isLocal && !t.trackId.startsWith('placeholder:')) {
                allRemoteUserIds.add(canonicalUserId(t.userId));
            }
        }
        for (const t of this.latestAudioTracks) {
            if (!t.isLocal) {
                allRemoteUserIds.add(canonicalUserId(t.userId));
            }
        }
        mm.beginPlaceholderSync();
        for (const userId of allRemoteUserIds) {
            if (usersWithRealVideo.has(userId) ||
                mm.hasRealVideoTracksForUser(userId)) {
                mm.hidePlaceholderTrack(userId);
            }
            else {
                mm.showPlaceholderTrack(userId);
            }
        }
        mm.endPlaceholderSync();
    }
    /**
     * Viewers do not composite, so when the broadcaster's video ends while
     * the stream goes on (the uplink congestion ladder's "video off", or a
     * camera turned off) they would be left with a blank canvas and audio.
     * Show the broadcaster's placeholder once their video has been seen and
     * then ended, until a video track returns.
     */
    syncViewerBroadcasterPlaceholder(mm, tracks) {
        const broadcasterId = this.streamrAppApi?.getBroadcasterUserId();
        if (!broadcasterId)
            return;
        const bid = canonicalUserId(broadcasterId);
        const hasVideo = tracks.some((t) => !t.isLocal &&
            !t.trackId.startsWith('placeholder:') &&
            canonicalUserId(t.userId) === bid);
        if (hasVideo) {
            this.viewerSawBroadcasterVideo = true;
            mm.hidePlaceholderTrack(bid);
            return;
        }
        if (!this.viewerSawBroadcasterVideo)
            return;
        const displayName = this.userManager?.getUser(broadcasterId)?.displayName ??
            'Broadcaster';
        mm.createPlaceholderTrack(bid, displayName);
        mm.showPlaceholderTrack(bid);
    }
    ensurePlaceholderTracksForActiveRemoteUsers(mm, videoTracks) {
        if (!this.userManager)
            return;
        const seen = new Set();
        for (const t of videoTracks) {
            if (!t.trackId.startsWith('placeholder:') && !t.isLocal) {
                seen.add(canonicalUserId(t.userId));
            }
        }
        for (const t of this.latestAudioTracks) {
            if (!t.isLocal) {
                seen.add(canonicalUserId(t.userId));
            }
        }
        for (const userId of seen) {
            mm.createPlaceholderTrack(userId, this.userManager.resolveDisplayName(userId));
        }
    }
    rebuildFilmStripGroups() {
        const currentStatus = this.userManager?.getCurrentUserRole();
        const ownTracksOnly = currentStatus === UserRole.SPEAKER ||
            currentStatus === UserRole.GUEST;
        const groupMap = new Map();
        const orderKeys = [];
        for (const vt of this.latestVideoTracks) {
            if (ownTracksOnly && !vt.isLocal)
                continue;
            if (!groupMap.has(vt.userId)) {
                groupMap.set(vt.userId, []);
                orderKeys.push(vt.userId);
            }
            const track = {
                type: 'video',
                trackId: vt.trackId,
                userId: vt.userId,
                name: this.userManager?.resolveDisplayName(vt.userId) ??
                    vt.userId,
                isYou: vt.isLocal,
                isLocal: vt.isLocal,
            };
            const tracks = groupMap.get(vt.userId);
            if (tracks)
                tracks.push(track);
        }
        for (const at of this.latestAudioTracks) {
            if (ownTracksOnly && !at.isLocal)
                continue;
            if (!groupMap.has(at.userId)) {
                groupMap.set(at.userId, []);
                orderKeys.push(at.userId);
            }
            const track = {
                type: 'audio',
                trackId: at.trackId,
                userId: at.userId,
                muted: at.muted,
                gain: at.gain,
                isLocal: at.isLocal,
            };
            const tracks = groupMap.get(at.userId);
            if (tracks)
                tracks.push(track);
        }
        const groups = orderKeys.map((userId) => ({
            userId,
            displayName: this.userManager?.resolveDisplayName(userId) ??
                userId,
            tracks: groupMap.get(userId) ?? [],
        }));
        this.output.setFilmStripGroups(groups);
    }
    evaluateLowEndConferencing() {
        if (!this.lowEndModeEnabled ||
            !this.userManager ||
            !this.streamrAppApi) {
            if (this.output.getLowEndConferencingActive()) {
                this.deactivateLowEndConferencing();
            }
            return;
        }
        const isCall = this.output.getSessionMode() === 'call' ||
            UserServiceOutput.getAcceptedParticipantType() ===
                AcceptedParticipantType.SPEAKERS;
        if (!isCall) {
            if (this.output.getLowEndConferencingActive()) {
                this.deactivateLowEndConferencing();
            }
            return;
        }
        const remoteParticipantCount = this.countRemoteCallParticipants();
        if (remoteParticipantCount < 2) {
            if (this.output.getLowEndConferencingActive()) {
                this.deactivateLowEndConferencing();
            }
            return;
        }
        if (!this.output.getLowEndConferencingActive()) {
            this.output.setLowEndConferencingActive(true);
        }
        this.updateLowEndFocusUser();
        this.updateLowEndParticipants();
    }
    countRemoteCallParticipants() {
        if (!this.userManager)
            return 0;
        let count = 0;
        for (const [, user] of this.userManager.getUsers()) {
            if (user.online &&
                (user.status === UserRole.SPEAKER ||
                    user.status === UserRole.COHOST)) {
                count++;
            }
        }
        return count;
    }
    updateLowEndFocusUser() {
        const mm = this.streamrAppApi?.getMediaManager();
        if (!mm)
            return;
        const dominantUserId = this.output.getDominantSpeakerUserId();
        if (dominantUserId) {
            mm.setLowEndFocusUserId(dominantUserId);
            return;
        }
        const fallbackUserId = this.getFirstRemoteSpeakerUserId();
        mm.setLowEndFocusUserId(fallbackUserId);
    }
    getFirstRemoteSpeakerUserId() {
        if (!this.userManager)
            return null;
        for (const [userId, user] of this.userManager.getUsers()) {
            if (user.online &&
                (user.status === UserRole.SPEAKER ||
                    user.status === UserRole.COHOST)) {
                return canonicalUserId(userId);
            }
        }
        return null;
    }
    updateLowEndParticipants() {
        if (!this.userManager)
            return;
        const participants = [];
        const speechStates = this.output.getRemoteSpeechStates();
        for (const [userId, user] of this.userManager.getUsers()) {
            if (!user.online ||
                (user.status !== UserRole.SPEAKER &&
                    user.status !== UserRole.COHOST)) {
                continue;
            }
            const uid = canonicalUserId(userId);
            participants.push({
                userId: uid,
                displayName: this.userManager.resolveDisplayName(userId) ?? userId,
                isSpeaking: speechStates[uid]?.isSpeaking ?? false,
            });
        }
        this.output.setLowEndParticipants(participants);
    }
    deactivateLowEndConferencing() {
        this.output.setLowEndConferencingActive(false);
        this.output.setLowEndParticipants([]);
        this.streamrAppApi?.getMediaManager().setLowEndFocusUserId(null);
    }
    notifyToggleAudio() {
        const muted = this.output.getAudioMuted();
        if (muted) {
            this.streamrAppApi
                ?.getMediaManager()
                .unmuteAudio()
                .catch(console.error);
        }
        else {
            this.streamrAppApi
                ?.getMediaManager()
                .muteAudio()
                .catch(console.error);
        }
    }
    notifyUserGesture() {
        this.streamrAppApi
            ?.getMediaManager()
            .unmuteAudio()
            .catch(console.error);
    }
    /**
     * Inbound-stream liveness, signalled by the NetworkingWorker only on
     * streaming-started / streaming-stopped transitions (the worker owns the
     * per-packet arrival tracking + 5s offline watchdog, so this no longer
     * fires per frame). `streaming` is true on the first packet of a stream and
     * false after the worker sees no packets for its offline timeout.
     */
    onStreamingStateChanged(streaming) {
        if (streaming) {
            if (!this.output.getWatchingStartedAt()) {
                this.output.setWatchingStartedAt(new Date());
            }
            this.output.setIsVideoStreaming(true);
        }
        else {
            this.output.setIsVideoStreaming(false);
        }
    }
    initializeBroadcastFromUrl() {
        const currentUrl = new URL(window.location.href);
        const broadcast = parseBroadcastFromUrl(currentUrl);
        if (!broadcast) {
            return false;
        }
        const cameraStreamIdParam = currentUrl.searchParams.get('cameraStreamId');
        const hashParams = new URLSearchParams(currentUrl.hash.replace(/^#/, ''));
        const cameraAesKeyParam = hashParams.get('cameraAesKey');
        const isCameraInvite = cameraStreamIdParam && cameraAesKeyParam;
        if (isCameraInvite) {
            const fullCameraStreamId = formFullStreamId(cameraStreamIdParam);
            this.pendingCameraOneToOneStreamUrl = `${fullCameraStreamId}#${cameraAesKeyParam}`;
        }
        const scheduledSession = ScheduleService.getInstance().getScheduledSession(broadcast.streamId);
        const roleProperties = scheduledSession !== undefined
            ? {
                myRole: 'broadcaster',
                broadcasterPrivateKey: scheduledSession.broadcasterPrivateKey,
            }
            : {
                myRole: 'viewer',
            };
        let resolvedStatus;
        if (isCameraInvite) {
            resolvedStatus = UserRole.CAMERA;
        }
        else if (roleProperties.myRole === 'broadcaster') {
            resolvedStatus = UserRole.COHOST;
        }
        else {
            const roleStore = new UserStore();
            const persistedRole = roleStore.getUserRole(broadcast.broadcasterUserId);
            if (persistedRole !== undefined &&
                persistedRole !== UserRole.VIEWER &&
                persistedRole !== UserRole.GUEST &&
                persistedRole !== UserRole.CAMERA) {
                resolvedStatus = persistedRole;
            }
            else {
                if (persistedRole === UserRole.GUEST ||
                    persistedRole === UserRole.CAMERA) {
                    console.log('[StreamrAppService] initializeBroadcastFromUrl: persisted role deferred to UserManager restoration', { persistedRole });
                }
                resolvedStatus = UserRole.VIEWER;
            }
        }
        UserService.getInstance().notifySetUserRole(resolvedStatus);
        const userStore = new UserStore();
        const storedBroadcasting = userStore.getBroadcastingEnabled(broadcast.streamId);
        const storedParticipantType = userStore.getAcceptedParticipantType(broadcast.streamId);
        if (storedBroadcasting !== undefined) {
            this.output.setBroadcastingEnabled(storedBroadcasting);
        }
        if (storedParticipantType !== undefined) {
            UserService.getInstance().notifyAcceptedParticipantTypeChanged(storedParticipantType);
            this.streamrAppApi
                ?.setAcceptedParticipantType(storedParticipantType)
                .catch(console.error);
        }
        BroadcastStateService.getInstance().notifySetActiveBroadcast({
            ...broadcast,
            ...roleProperties,
        });
        return true;
    }
    createStreamrAppApi(activeBroadcast) {
        const initialUserRole = activeBroadcast.myRole === 'viewer'
            ? UserRole.VIEWER
            : UserRole.COHOST;
        const streamrAppApi = new StreamrAppApi({
            streamId: activeBroadcast.streamId,
            broadcasterUserId: toUserIdRaw(activeBroadcast.broadcasterUserId),
            aesKey: hexToBinary(activeBroadcast.aesKey),
            initialUserRole,
        });
        if (activeBroadcast.myRole !== 'viewer') {
            const storedDisplayName = new UserStore().getDisplayName();
            streamrAppApi
                .getMediaManager()
                .setLocalUserId(activeBroadcast.broadcasterUserId, storedDisplayName);
            if (storedDisplayName) {
                streamrAppApi.setDisplayName(storedDisplayName);
            }
            const ografManager = OgrafManager.getInstance();
            ografManager.setMediaManager(streamrAppApi.getMediaManager());
            void OgrafService.getInstance().initialize();
        }
        streamrAppApi.getMediaManager().on('muteChange', (muted) => {
            this.output.setAudioMuted(muted);
        });
        streamrAppApi
            .getMediaManager()
            .on('displayOutputChanged', (event) => {
            this.onDisplayOutputChanged(event);
        });
        streamrAppApi
            .getMediaManager()
            .on('audioContextBlocked', (blocked) => {
            this.output.setAudioContextBlocked(blocked);
        });
        streamrAppApi
            .getMediaManager()
            .on('userMutedChange', (userMuted) => {
            this.output.setUserMutedAudio(userMuted);
        });
        streamrAppApi
            .getMediaManager()
            .on('speechStateChanged', (isSpeaking) => {
            this.output.setLocalIsSpeaking(isSpeaking);
        });
        streamrAppApi
            .getMediaManager()
            .on('dominantSpeakerChanged', (_userSessionId, userId) => {
            this.output.setDominantSpeakerUserId(userId);
            this.evaluateLowEndConferencing();
        });
        streamrAppApi
            .getMediaManager()
            .on('remoteSpeechStateChanged', (_userSessionId, userId, isSpeaking, audioLevel) => {
            this.output.setRemoteSpeechState(userId, {
                isSpeaking,
                audioLevel,
            });
            if (this.output.getLowEndConferencingActive()) {
                this.updateLowEndParticipants();
            }
        });
        streamrAppApi.on('streamingStateChanged', (streaming) => {
            this.onStreamingStateChanged(streaming);
        });
        // viewer quality: preference/memory from this device, then the
        // engine's decisions mirrored into the Output
        const viewerQualityStore = ViewerQualityStore.getInstance();
        const viewerQuality = StreamrAppApi.readViewerQualityOptions({
            storedMode: viewerQualityStore.getMode(),
            remembered: viewerQualityStore.getRemembered(),
        });
        console.log('[viewer-quality]', JSON.stringify({ ev: 'options', ...viewerQuality }));
        streamrAppApi.setContentQualityMode(viewerQuality.mode, viewerQuality.initial, viewerQuality.tune);
        streamrAppApi.on('contentQualityChanged', (state) => {
            this.output.setContentQuality(state);
        });
        this.output.setContentQuality(streamrAppApi.getContentQualityState());
        if (process.env.NODE_ENV !== 'production') {
            // Playwright drives the quality switches through this hook
            window.__streamrtvTest = {
                setViewerQualityMode: (mode) => this.notifySetViewerQualityMode(mode),
                retryContentViewing: () => this.notifyRetryContentViewing(),
                failContentViewing: (reason) => streamrAppApi.failContentViewing(reason),
                simulateDownlinkDelay: (offsetMs, partition) => streamrAppApi.setDownlinkDelayTestOffset(offsetMs, partition),
            };
        }
        streamrAppApi.on('recordingStateChanged', (state) => {
            RecordingService.getNotificationInterface().notifyRecordingState(state);
        });
        streamrAppApi.on('compositeSuspendChanged', (suspended) => {
            // low-only mode: tell viewers at once so they move partitions
            this.highQualityAvailable = !suspended;
            console.log('StreamrAppService: high-quality composite', {
                available: this.highQualityAvailable,
            });
            this.publishBroadcastMetadataNow();
        });
        streamrAppApi.on('broadcastMetadataChanged', (broadcastMetadata) => {
            if (this.currentStreamId) {
                ViewerCountService.getInstance().notifyViewerCountChangedForActiveStream(broadcastMetadata.viewerCount);
                PurchaseService.getInstance().notifyProductsOfActiveBroadcastChanged(broadcastMetadata.products, false);
                UserService.getInstance().notifyAcceptedParticipantTypeChanged(broadcastMetadata.acceptedParticipantType);
                this.streamrAppApi
                    ?.setAcceptedParticipantType(broadcastMetadata.acceptedParticipantType)
                    .catch(console.error);
                this.output.setBroadcastMetadataReceived(true);
                LobbyService.getInstance().notifyBroadcastMetadataReceived(broadcastMetadata.acceptedParticipantType);
                this.streamrAppApi?.setLowQualityPartition(broadcastMetadata.lowQualityPartition);
                this.streamrAppApi?.setHighQualityAvailable(broadcastMetadata.highQualityAvailable ?? true);
            }
        });
        streamrAppApi
            .getMediaManager()
            .on('videoPropertiesChanged', (videoProperties) => {
            VideoPropertiesService.getNotificationInterface().notifyVideoPropertiesChanged(videoProperties);
        });
        streamrAppApi
            .getMediaManager()
            .on('activePublishersChanged', (publishers) => {
            this.output.setActivePublishers(publishers);
        });
        streamrAppApi
            .getMediaManager()
            .on('activeVideoTracksChanged', (tracks) => {
            if (activeBroadcast.myRole === 'viewer' &&
                tracks.length > 0 &&
                !this.output.getWatchingStartedAt()) {
                this.output.setWatchingStartedAt(new Date());
                this.output.setIsVideoStreaming(true);
            }
            const previousTracks = this.latestVideoTracks;
            this.output.setActiveVideoTracks(tracks);
            this.latestVideoTracks = tracks;
            this.rebuildFilmStripGroups();
            const currentTrackIds = new Set(tracks.map((t) => t.trackId));
            const disappeared = previousTracks.filter((t) => !currentTrackIds.has(t.trackId));
            if (disappeared.length > 0) {
                this.handleTrackDisappearanceFallback(disappeared, tracks, currentTrackIds);
            }
            if (this.roleComposites(streamrAppApi.getUserRole())) {
                const previousTrackIds = new Set(previousTracks.map((t) => t.trackId));
                const hasNewTracks = tracks.some((t) => !previousTrackIds.has(t.trackId));
                if (hasNewTracks) {
                    const displayScene = SceneService.getInstance().getDisplayScene();
                    this.buildAndPushDisplayConfig(displayScene);
                }
            }
            this.syncPlaceholderVisibility(streamrAppApi.getMediaManager(), tracks);
        });
        streamrAppApi
            .getMediaManager()
            .on('activeAudioTracksChanged', (tracks) => {
            this.output.setActiveAudioTracks(tracks);
            this.latestAudioTracks = tracks;
            this.rebuildFilmStripGroups();
            this.syncPlaceholderVisibility(streamrAppApi.getMediaManager(), this.latestVideoTracks);
        });
        // Set up services when StreamrWrapper is ready
        streamrAppApi.on('streamrWrapperReady', (streamrWrapper) => {
            const pluginApiService = PluginApiService.getInstance();
            pluginApiService.setStreamrWrapper(streamrWrapper);
            const purchaseService = PurchaseService.getInstance();
            purchaseService.setStreamrWrapper(streamrWrapper);
            // Set up UserManager
            if (this.currentBroadcast) {
                const hostUserId = toUserId(toUserIdRaw(this.currentBroadcast.broadcasterUserId));
                const ownUserId = streamrWrapper.getMyUserId();
                const ownPrivateKey = this.currentBroadcast
                    .broadcasterPrivateKey
                    ? hexToBinary(this.currentBroadcast.broadcasterPrivateKey)
                    : hexToBinary(KeyStore.getInstance().getPrivateKey(this.currentBroadcast.streamId));
                if (ownUserId !== hostUserId &&
                    this.roleAddsLocalTracks(streamrAppApi.getUserRole())) {
                    streamrAppApi
                        .getMediaManager()
                        .setLocalUserId(ownUserId, new UserStore().getDisplayName());
                }
                const storedSpeakerSecret = ownUserId !== hostUserId
                    ? new UserStore().getSpeakerSecret(hostUserId)
                    : undefined;
                const userManager = new UserManager(hostUserId, ownUserId, ownPrivateKey, storedSpeakerSecret, undefined, this.currentStreamId);
                this.userManager = userManager;
                userManager.setStreamrWrapper(streamrWrapper);
                UserService.getInstance().setUserManager(userManager);
                streamrAppApi.setUserManager(userManager);
                userManager.on('displayNamesChanged', (displayNames) => {
                    this.rebuildFilmStripGroups();
                    const mm = streamrAppApi.getMediaManager();
                    for (const [uid, name] of displayNames) {
                        mm.updatePlaceholderTrack(uid, name);
                    }
                });
                userManager.on('ownRoleChanged', (newRole, oneToOneStreamUrl) => {
                    // Each role change invalidates the async tail of
                    // any previous transition. Without this, leaving
                    // the stage and re-requesting within ~10s let the
                    // stale VIEWER chain (removeAllLocalTracks +
                    // leaveOwnOneToOneStream after its content-audio
                    // wait) destroy the new GUEST session's tracks
                    // and one-to-one stream.
                    this.roleTransitionEpoch++;
                    const epoch = this.roleTransitionEpoch;
                    const stale = () => epoch !== this.roleTransitionEpoch;
                    this.streamrAppApi
                        ?.getMediaManager()
                        .setExcludeLocalTracksFromMainCanvas(newRole === UserRole.GUEST)
                        .catch(console.error);
                    this.streamrAppApi
                        ?.getMediaManager()
                        .setSpeechDetectionEnabled(this.roleUsesSpeechDetection(newRole));
                    if (this.roleAddsLocalTracks(newRole) &&
                        ownUserId !== hostUserId &&
                        !this.streamrAppApi
                            ?.getMediaManager()
                            .getLocalUserId()) {
                        this.streamrAppApi
                            ?.getMediaManager()
                            .setLocalUserId(ownUserId, new UserStore().getDisplayName());
                    }
                    // Set resolution synchronously before the async
                    // setUserRole() call. React's useEffect will fire
                    // notifyStartCaptureRequested() as soon as the
                    // role propagates to the UI, so the resolution
                    // must already be set when getUserMedia runs.
                    if (this.roleAddsLocalTracks(newRole)) {
                        const profile = this.getVideoProfileForRole(newRole);
                        this.mediaService.setVideoResolution(profile.width, profile.height);
                    }
                    this.streamrAppApi
                        ?.setUserRole(newRole)
                        .then(async () => {
                        if (stale())
                            return;
                        if (newRole === UserRole.GUEST &&
                            oneToOneStreamUrl &&
                            this.streamrAppApi) {
                            await this.streamrAppApi.joinOneToOnePublisher(oneToOneStreamUrl);
                            const audioReady = await this.streamrAppApi.waitForOneToOneAudioReady(10000);
                            if (stale())
                                return;
                            if (audioReady) {
                                await this.streamrAppApi.stopContentAudio();
                            }
                            else {
                                // Keep content audio playing
                                // (echo beats silence) and switch
                                // as soon as N-1 arrives, however
                                // late: on a slow uplink the host's
                                // N-1 mix took 80 s to form and a
                                // capped wait left the guest hearing
                                // themselves for the whole session.
                                console.warn('[StreamrAppService] one-to-one audio not ready after accept — deferring content audio stop until it arrives');
                                const deferredAt = Date.now();
                                void this.streamrAppApi
                                    .waitForOneToOneAudioReady(0)
                                    .then(async (ready) => {
                                    if (!ready || stale())
                                        return;
                                    console.log(`[StreamrAppService] one-to-one audio arrived ${Math.round((Date.now() - deferredAt) / 1000)}s after the deferral — stopping content audio`);
                                    await this.streamrAppApi?.stopContentAudio();
                                });
                            }
                        }
                        if (newRole === UserRole.VIEWER &&
                            this.streamrAppApi) {
                            await this.streamrAppApi.resumeContentAudio();
                            await this.streamrAppApi.waitForContentAudioReady(10000);
                        }
                        if (stale())
                            return;
                        if (!this.roleAddsLocalTracks(newRole)) {
                            await this.streamrAppApi
                                ?.getMediaManager()
                                .removeAllLocalTracks();
                        }
                        if (this.roleComposites(newRole)) {
                            this.ensurePlaceholderTracksForExistingUsers();
                            void this.startSpeakerPublishing();
                        }
                        else if (this.roleAddsLocalTracks(newRole)) {
                            await this.streamrAppApi
                                ?.getMediaManager()
                                .startPublishingViewerStream({
                                useDirectTracks: true,
                            });
                        }
                        if (stale())
                            return;
                        if (newRole === UserRole.VIEWER) {
                            await this.streamrAppApi?.leaveOwnOneToOneStream();
                        }
                        this.applyDisplayOutputPolicy(newRole);
                    })
                        .catch(console.error);
                });
                const restoredRole = userManager.getCurrentUserRole();
                if (restoredRole !== this.streamrAppApi?.getUserRole()) {
                    console.log('[StreamrAppService] Role mismatch detected, reconciling', {
                        restoredRole,
                        engineRole: this.streamrAppApi?.getUserRole(),
                    });
                    this.streamrAppApi
                        ?.getMediaManager()
                        .setExcludeLocalTracksFromMainCanvas(restoredRole === UserRole.GUEST)
                        .catch(console.error);
                    this.streamrAppApi
                        ?.getMediaManager()
                        .setSpeechDetectionEnabled(this.roleUsesSpeechDetection(restoredRole));
                    if (this.roleAddsLocalTracks(restoredRole) &&
                        ownUserId !== hostUserId &&
                        !this.streamrAppApi
                            ?.getMediaManager()
                            .getLocalUserId()) {
                        this.streamrAppApi
                            ?.getMediaManager()
                            .setLocalUserId(ownUserId, new UserStore().getDisplayName());
                    }
                    if (this.roleAddsLocalTracks(restoredRole)) {
                        const profile = this.getVideoProfileForRole(restoredRole);
                        this.mediaService.setVideoResolution(profile.width, profile.height);
                    }
                    else {
                        this.streamrAppApi
                            ?.getMediaManager()
                            .removeAllLocalTracks()
                            .catch(console.error);
                    }
                    this.streamrAppApi
                        ?.setUserRole(restoredRole)
                        .then(async () => {
                        await this.engineStartedPromise;
                        if ((restoredRole === UserRole.VIEWER ||
                            restoredRole === UserRole.GUEST) &&
                            this.streamrAppApi) {
                            await this.streamrAppApi.activateContentPartition();
                        }
                        if (restoredRole === UserRole.GUEST &&
                            this.streamrAppApi) {
                            // Unmute BEFORE waiting for one-to-one
                            // audio: while the AudioContext is
                            // suspended the networking worker drops
                            // every inbound audio frame, so
                            // playbackStarted (and thus
                            // oneToOneAudioReady) can never fire —
                            // waiting first deadlocks until timeout.
                            await this.streamrAppApi
                                .getMediaManager()
                                .unmuteAudio();
                            console.log('[StreamrAppService] GUEST restore: audio unmuted');
                            const storedUrl = new UserStore().getOneToOneStreamUrl(hostUserId);
                            if (storedUrl) {
                                console.log('[StreamrAppService] GUEST restore: joining one-to-one publisher', { url: storedUrl.slice(0, 40) });
                                await this.streamrAppApi.joinOneToOnePublisher(storedUrl, 
                                // the host still sits on the
                                // stored stream's part: a silent
                                // part is re-rolled early
                                { existingPart: true });
                                // 6 s covers every observed success
                                // path (healthy ≤0.7 s, rotation-
                                // rescued ~4 s); waiting longer only
                                // delays the re-request round that
                                // rescues the pathological tail. The
                                // re-request is idempotent: the host
                                // reuses the stream if media is
                                // flowing by then.
                                const audioReady = await this.streamrAppApi.waitForOneToOneAudioReady(6000);
                                if (audioReady) {
                                    console.log('[StreamrAppService] GUEST restore: one-to-one audio ready, stopping content audio');
                                    await this.streamrAppApi.stopContentAudio();
                                }
                                else {
                                    console.warn('[StreamrAppService] GUEST restore: one-to-one audio timed out, re-requesting permission');
                                    this.userManager?.requestPermission(UserRole.GUEST);
                                }
                            }
                            else {
                                console.warn('[StreamrAppService] GUEST restore: no stored one-to-one URL, re-requesting permission');
                                this.userManager?.requestPermission(UserRole.GUEST);
                            }
                        }
                        if (this.roleComposites(restoredRole)) {
                            this.ensurePlaceholderTracksForExistingUsers();
                            void this.startSpeakerPublishing();
                        }
                        else if (this.roleAddsLocalTracks(restoredRole)) {
                            await this.streamrAppApi
                                ?.getMediaManager()
                                .startPublishingViewerStream({
                                useDirectTracks: true,
                            });
                        }
                        this.applyDisplayOutputPolicy(restoredRole);
                    })
                        .catch(console.error);
                }
                userManager.on('oneToOneStreamCreated', (userId, oneToOneStreamUrl) => {
                    this.streamrAppApi
                        ?.joinOneToOneReceiver(userId, oneToOneStreamUrl)
                        .catch(console.error);
                });
                userManager.on('userRoleChanged', (userId, newStatus, displayName) => {
                    if (newStatus === UserRole.GUEST) {
                        this.streamrAppApi
                            ?.startN1AudioForGuest(userId)
                            .catch(console.error);
                    }
                    else {
                        this.streamrAppApi
                            ?.stopN1AudioForGuest(userId)
                            .catch(console.error);
                    }
                    if (!this.roleComposites(streamrAppApi.getUserRole()))
                        return;
                    const mm = streamrAppApi.getMediaManager();
                    if (newStatus === UserRole.SPEAKER ||
                        newStatus === UserRole.COHOST) {
                        mm.createPlaceholderTrack(userId, displayName);
                        if (!mm.hasRealVideoTracksForUser(userId)) {
                            mm.showPlaceholderTrack(userId);
                        }
                    }
                    else if (newStatus === UserRole.VIEWER) {
                        this.streamrAppApi
                            ?.leaveOneToOneStream(userId)
                            .catch(console.error);
                        mm.handleUserOffline(userId);
                        mm.destroyPlaceholderTrack(userId);
                    }
                    this.evaluateLowEndConferencing();
                });
                userManager.on('userCameOnline', (userId, displayName) => {
                    const user = userManager.getUser(userId);
                    if (this.broadcastMetadataIntervalId !== undefined) {
                        this.publishBroadcastMetadataNow();
                        streamrAppApi.publishHeartbeatNow();
                    }
                    if (!this.roleComposites(streamrAppApi.getUserRole()))
                        return;
                    if (user &&
                        (user.status === UserRole.SPEAKER ||
                            user.status === UserRole.COHOST)) {
                        const mm = streamrAppApi.getMediaManager();
                        mm.createPlaceholderTrack(userId, displayName);
                        if (!mm.hasRealVideoTracksForUser(userId)) {
                            mm.showPlaceholderTrack(userId);
                        }
                    }
                    this.evaluateLowEndConferencing();
                });
                userManager.on('userWentOffline', (userId) => {
                    const mm = streamrAppApi.getMediaManager();
                    mm.handleUserOffline(userId);
                    if (this.roleComposites(streamrAppApi.getUserRole())) {
                        mm.destroyPlaceholderTrack(userId);
                    }
                    this.evaluateLowEndConferencing();
                });
            }
        });
        // Forward plugin messages to PluginApiService
        streamrAppApi.on('pluginMessage', (_message, _publisherId) => {
            // PluginApiService handles this through its StreamrWrapper listener
            // No action needed here since it's already set up in setStreamrWrapper
        });
        // Forward purchase confirmed messages to PurchaseService
        streamrAppApi.on('purchaseConfirmedMessage', (message, _publisherId) => {
            PurchaseService.getInstance().onPurchaseConfirmedMessage(message);
        });
        return streamrAppApi;
    }
    // Fire-and-forget call to the sponsorship backend (if configured).
    // Sponsorships make operator nodes stake on the stream and join it,
    // so the stream stays available beyond the participants' own nodes.
    // The backend dedupes, so repeated calls for a stream are safe.
    callSponsorshipBackend(endpoint, streamId) {
        const sponsorshipBackendUrl = Env.SPONSORSHIP_BACKEND_URL;
        if (sponsorshipBackendUrl === undefined)
            return;
        executeSoon(async () => {
            try {
                await fetch(`${sponsorshipBackendUrl}/api/${endpoint}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ streamId }),
                });
            }
            catch (e) {
                console.error(`Error calling sponsorship backend ${endpoint}`, e);
            }
        });
    }
    prepareRendering(activeBroadcast) {
        if (this.renderingReadyPromise)
            return;
        console.log('StreamrAppService: prepareRendering() called for stream:', activeBroadcast.streamId);
        this.currentStreamId = activeBroadcast.streamId;
        this.currentBroadcast = activeBroadcast;
        this.streamrAppApi?.stop().catch(() => undefined);
        if (activeBroadcast.myRole === 'broadcaster') {
            // Optional optimization for the create-sponsorship call on
            // go-live: that call creates the stream if needed, which is
            // slow, so kick stream creation off as early as possible.
            this.callSponsorshipBackend('create-stream', activeBroadcast.streamId);
        }
        const savedCanvas = this.pendingCanvas;
        this.pendingCanvas = undefined;
        this.canvasReadyPromise = new Promise((resolve) => {
            this.canvasReadyResolve = resolve;
        });
        this.renderingReadyPromise = new Promise((resolve) => {
            this.renderingReadyResolve = resolve;
        });
        this.engineStartedPromise = new Promise((resolve) => {
            this.engineStartedResolve = resolve;
        });
        console.log('StreamrAppService: Creating StreamrAppApi');
        this.streamrAppApi = this.createStreamrAppApi(activeBroadcast);
        executeSoon(async () => {
            if (!this.streamrAppApi)
                return;
            const privateKey = activeBroadcast.broadcasterPrivateKey
                ? hexToBinary(activeBroadcast.broadcasterPrivateKey)
                : hexToBinary(KeyStore.getInstance().getPrivateKey(activeBroadcast.streamId));
            console.log('StreamrAppService: Starting streamrAppApi with private key', activeBroadcast.broadcasterPrivateKey
                ? 'from broadcaster'
                : 'from KeyStore');
            await this.streamrAppApi.start(privateKey);
            this.engineStartedResolve?.();
            const currentApt = UserServiceOutput.getAcceptedParticipantType();
            if (currentApt !== 0) {
                await this.streamrAppApi.setAcceptedParticipantType(currentApt);
            }
            if (this.pendingCameraOneToOneStreamUrl) {
                const cameraStreamUrl = this.pendingCameraOneToOneStreamUrl;
                this.pendingCameraOneToOneStreamUrl = undefined;
                await this.streamrAppApi.joinOneToOnePublisher(cameraStreamUrl);
            }
            console.log('StreamrAppService: StreamrAppApi started');
            this.output.setReady(true);
            this.broadcastActivated = true;
            LobbyService.getInstance().notifyEngineReady();
            const currentRole = this.streamrAppApi.getUserRole();
            if (savedCanvas && !this.pendingCanvas) {
                await this.streamrAppApi
                    .getMediaManager()
                    .setDisplayCanvas(savedCanvas);
                this.pendingCanvas = savedCanvas;
                this.canvasReadyResolve?.();
            }
            this.streamrAppApi
                .getMediaManager()
                .setSpeechDetectionEnabled(this.roleUsesSpeechDetection(currentRole));
            this.applyDisplayOutputPolicy(currentRole);
            if (this.roleAddsLocalTracks(currentRole)) {
                const profile = this.getVideoProfileForRole(currentRole);
                this.mediaService.setVideoResolution(profile.width, profile.height);
                await this.onMediaStreamChanged();
            }
            if (this.roleComposites(currentRole)) {
                const sceneService = SceneService.getInstance();
                const displayScene = sceneService.getDisplayScene();
                this.buildAndPushDisplayConfig(displayScene);
            }
            console.log('StreamrAppService: Rendering pipeline ready');
            this.renderingReadyResolve?.();
        });
    }
    activateBroadcast(activeBroadcast) {
        console.log('StreamrAppService: activateBroadcast() called for stream:', activeBroadcast.streamId);
        if (activeBroadcast.myRole === 'viewer') {
            this.prepareRendering(activeBroadcast);
            return;
        }
        if (!this.renderingReadyPromise) {
            this.prepareRendering(activeBroadcast);
        }
        // Remember that this host session is live so a browser refresh
        // resumes the broadcast automatically (LobbyService checks this).
        if (activeBroadcast.myRole === 'broadcaster') {
            new UserStore().setHostWasLive(activeBroadcast.streamId, true);
            this.liveStreamIdForRecovery = activeBroadcast.streamId;
            // Create a sponsorship for the stream so that operator nodes
            // join this stream.
            this.callSponsorshipBackend('create-sponsorship', activeBroadcast.streamId);
        }
        executeSoon(async () => {
            await this.renderingReadyPromise;
            console.log('StreamrAppService: Waiting for canvas readiness');
            await this.canvasReadyPromise;
            if (!this.streamrAppApi)
                return;
            const isCallMode = this.output.getSessionMode() === 'call';
            if (isCallMode) {
                this.streamrAppApi
                    .setContentPublishingEnabled(this.output.getBroadcastingEnabled())
                    .catch(console.error);
            }
            console.log('StreamrAppService: Canvas ready, starting viewer stream publishing', { isCallMode });
            // the low-quality composite (desktop broadcasters): join its
            // partition first so the entrypoint is registered by the first
            // keyframe
            const lowQualityOptions = this.streamrAppApi.getLowQualityStreamOptions();
            const lowQuality = !isCallMode && lowQualityOptions.enabled;
            console.log('StreamrAppService: low-quality composite', {
                ...lowQualityOptions,
                isCallMode,
            });
            if (lowQuality) {
                await this.streamrAppApi.activateLowContentPartition();
            }
            const mediaManager = this.streamrAppApi.getMediaManager();
            await mediaManager.startPublishingViewerStream({
                useDirectTracks: isCallMode,
                lowQuality,
            });
            this.lowQualityCompositeActive =
                mediaManager.isLowQualityCompositeActive();
            this.output.setIsVideoStreaming(true);
            const sceneService = SceneService.getInstance();
            this.buildAndPushLiveConfig(sceneService.getLiveScene());
            this.startBroadcastMetadataInterval();
            console.log('StreamrAppService: Broadcast activation complete');
        });
    }
    liveStreamIdForRecovery;
    /** the host publishes the low-quality composite on the low partition */
    lowQualityCompositeActive = false;
    /** false while the host's uplink only carries the low-quality composite */
    highQualityAvailable = true;
    // Incremented on every own-role change; stale async transition chains
    // check it and abort instead of tearing down the newer transition.
    roleTransitionEpoch = 0;
    deactivateBroadcast() {
        if (this.liveStreamIdForRecovery) {
            new UserStore().setHostWasLive(this.liveStreamIdForRecovery, false);
            this.liveStreamIdForRecovery = undefined;
        }
        this.stopBroadcastMetadataInterval();
        this.lowQualityCompositeActive = false;
        this.highQualityAvailable = true;
        executeSoon(async () => {
            if (this.streamrAppApi) {
                await this.streamrAppApi
                    .getMediaManager()
                    .stopPublishingViewerStream();
                await this.streamrAppApi.deactivateLowContentPartition();
            }
            this.output.setIsVideoStreaming(false);
            this.clearThumbnailRendering();
            this.transferredThumbnailCanvasIds.clear();
            this.thumbnailTransferRetries = {};
            this.activeThumbnailParticipantIds = [];
            DestinationsService.getInstance().resetAllDestinations();
        });
    }
    destroyRendering() {
        this.stopBroadcastMetadataInterval();
        this.canvasReadyResolve?.();
        this.canvasReadyResolve = undefined;
        this.canvasReadyPromise = undefined;
        this.renderingReadyResolve?.();
        this.renderingReadyResolve = undefined;
        this.renderingReadyPromise = undefined;
        this.pendingCanvas = undefined;
        const directTrack = this.output.getDirectVideoTrack();
        this.output.setDirectVideoTrack(null);
        directTrack?.stop();
        executeSoon(async () => {
            await this.streamrAppApi?.getMediaManager().muteAudio();
            this.output.setAudioMuted(true);
            this.output.setReady(false);
            this.output.setIsVideoStreaming(false);
            this.output.setBroadcastMetadataReceived(false);
            this.broadcastActivated = false;
            this.clearThumbnailRendering();
            this.transferredThumbnailCanvasIds.clear();
            this.thumbnailTransferRetries = {};
            this.activeThumbnailParticipantIds = [];
            await this.streamrAppApi?.stop();
            this.streamrAppApi = undefined;
            this.userManager = undefined;
            this.pendingCameraOneToOneStreamUrl = undefined;
            this.publishedDisplayAudioTrackId = undefined;
            this.engineStartedResolve = undefined;
            this.engineStartedPromise = undefined;
            DestinationsService.getInstance().resetAllDestinations();
            this.currentStreamId = undefined;
        });
    }
    onMediaStreamChanged = async () => {
        if (this.broadcastActivated && this.streamrAppApi) {
            const currentRole = this.streamrAppApi.getUserRole();
            if (!this.roleAddsLocalTracks(currentRole))
                return;
            const manager = this.streamrAppApi.getMediaManager();
            const videoTrack = this.mediaService.getSourceVideoTrack();
            if (this.pendingCanvas) {
                await manager.addVideoTrack(videoTrack);
                if (videoTrack) {
                    const profile = this.getVideoProfileForRole(currentRole);
                    manager.setPerTrackVideoBitrate(videoTrack.id, profile.bitrate);
                }
            }
            const audioTrack = this.mediaService.getSourceAudioTrack();
            if (audioTrack) {
                await manager.addAudioTrack(audioTrack);
            }
            await this.syncDisplayAudioTrack(manager);
        }
    };
    /**
     * A shared tab's audio travels as an extra local audio track beside the
     * microphone, so the sharer keeps talking over it. It is published when
     * a share with audio starts and withdrawn when the share ends, so the
     * host sees the track finish instead of a dead track lingering in the
     * guest's own-audio set.
     */
    async syncDisplayAudioTrack(manager) {
        const displayAudioTrack = this.mediaService.getDisplayAudioTrack();
        const published = this.publishedDisplayAudioTrackId;
        if (published !== undefined && published !== displayAudioTrack?.id) {
            this.publishedDisplayAudioTrackId = undefined;
            await manager.removeLocalAudioTrack(published);
        }
        if (displayAudioTrack && displayAudioTrack.id !== published) {
            this.publishedDisplayAudioTrackId = displayAudioTrack.id;
            await manager.addAdditionalAudioTrack(displayAudioTrack);
        }
    }
    onAdditionalTrackAdded = async (track) => {
        if (!this.broadcastActivated || !this.streamrAppApi) {
            return;
        }
        const currentRole = this.streamrAppApi.getUserRole();
        if (!this.roleAddsLocalTracks(currentRole))
            return;
        const manager = this.streamrAppApi.getMediaManager();
        if (track.kind === 'video') {
            await manager.addAdditionalVideoTrack(track);
            const profile = this.getVideoProfileForRole(currentRole);
            manager.setPerTrackVideoBitrate(track.id, profile.bitrate);
        }
        else if (track.kind === 'audio') {
            await manager.addAdditionalAudioTrack(track);
        }
    };
    onAdditionalTrackRemoved = async (trackId) => {
        if (!this.broadcastActivated || !this.streamrAppApi) {
            return;
        }
        if (!this.roleAddsLocalTracks(this.streamrAppApi.getUserRole()))
            return;
        const manager = this.streamrAppApi.getMediaManager();
        await manager.removeLocalVideoTrack(trackId);
        await manager.removeLocalAudioTrack(trackId);
    };
    notifyAddCamera(deviceId) {
        this.mediaService.notifyAddCamera(deviceId);
    }
    notifyAddMicrophone(deviceId) {
        this.mediaService.notifyAddMicrophone(deviceId);
    }
    notifyShareScreen() {
        this.mediaService.notifyShareScreen();
    }
    notifyRemoveLocalTrack(trackId) {
        this.mediaService.notifyRemoveLocalTrack(trackId);
    }
    notifySetDisplayName(name) {
        // Persist immediately: the name is typically entered in the lobby
        // while the engine (and its UserManager) is still starting, and
        // consumers such as initializing plugin frames read it from the
        // store before the engine gets around to writing it.
        new UserStore().setDisplayName(name);
        this.userManager?.setLocalDisplayName(name);
        this.streamrAppApi?.setDisplayName(name);
        const localUserId = this.streamrAppApi
            ?.getMediaManager()
            .getLocalUserId();
        if (localUserId) {
            this.streamrAppApi
                ?.getMediaManager()
                .updatePlaceholderTrack(localUserId, name);
        }
        PluginApiService.getInstance().notifyDisplayNameChanged(name);
    }
    /**
     * Creates (or reuses) a session and makes this client its broadcaster:
     * generates streamId/keys for a new session, persists it via
     * ScheduleService, applies the session mode and sets the active
     * broadcast. Navigation is left to the caller via onActivate, which
     * receives the session's broadcast path — an SPA navigation there lands
     * on the broadcaster view (the in-memory active broadcast carries the
     * role, so no URL re-parsing happens).
     */
    notifyActivateSession(savedSession, mode, options) {
        let session;
        if (savedSession === undefined) {
            const streamId = generateStreamId();
            const { privateKey, userId, aesKey } = generateKeysAndUserId();
            session = {
                id: generateSessionId(),
                name: options?.name ?? 'Session',
                startsAt: Date.now(),
                streamId,
                broadcasterPrivateKey: privateKey,
                broadcasterUserId: userId,
                aesKey,
            };
            ScheduleService.getInstance().notifyAddScheduledSession(session);
        }
        else {
            session = savedSession;
        }
        this.notifySetSessionMode(mode, session.streamId);
        BroadcastStateService.getInstance().notifySetActiveBroadcast({
            ...session,
            myRole: 'broadcaster',
        });
        options?.onActivate?.(formBroadcastPath(session), session);
    }
    notifySetSessionMode(mode, streamId) {
        this.output.setSessionMode(mode);
        const store = new UserStore();
        const storedBroadcasting = store.getBroadcastingEnabled(streamId);
        const storedParticipantType = store.getAcceptedParticipantType(streamId);
        if (mode === 'call') {
            const broadcasting = storedBroadcasting ?? false;
            const participantType = storedParticipantType ?? 1;
            this.output.setBroadcastingEnabled(broadcasting);
            store.setBroadcastingEnabled(streamId, broadcasting);
            UserService.getInstance().notifyAcceptedParticipantTypeChanged(participantType);
            store.setAcceptedParticipantType(streamId, participantType);
            this.streamrAppApi
                ?.setAcceptedParticipantType(participantType)
                .catch(console.error);
        }
        else {
            const broadcasting = storedBroadcasting ?? true;
            const participantType = storedParticipantType ?? 2;
            this.output.setBroadcastingEnabled(broadcasting);
            store.setBroadcastingEnabled(streamId, broadcasting);
            UserService.getInstance().notifyAcceptedParticipantTypeChanged(participantType);
            store.setAcceptedParticipantType(streamId, participantType);
            this.streamrAppApi
                ?.setAcceptedParticipantType(participantType)
                .catch(console.error);
        }
    }
    async notifyCreateCameraInviteUrl() {
        const broadcast = this.currentBroadcast;
        if (!broadcast || !this.streamrAppApi) {
            return undefined;
        }
        const cameraStreamId = generateStreamId();
        const cameraAesKey = crypto.getRandomValues(new Uint8Array(32));
        const oneToOneStreamUrl = `${cameraStreamId}#${binaryToHex(cameraAesKey, false)}`;
        const cameraUserId = `camera-${formShortStreamId(cameraStreamId)}`;
        await this.streamrAppApi.joinOneToOneReceiver(cameraUserId, oneToOneStreamUrl);
        const broadcastUrl = formBroadcastUrl(broadcast);
        const url = new URL(broadcastUrl);
        url.searchParams.set('cameraStreamId', formShortStreamId(cameraStreamId));
        const existingHash = url.hash.replace(/^#/, '');
        url.hash = `${existingHash}&cameraAesKey=${binaryToHex(cameraAesKey, false)}`;
        return url.toString();
    }
    notifyViewerLobbyDismissed() {
        this.streamrAppApi?.activateContentPartition().catch(console.error);
    }
    /** Viewer quality menu: auto (relay-capacity driven), or pin high/low. */
    notifySetViewerQualityMode(mode) {
        ViewerQualityStore.getInstance().setMode(mode);
        this.streamrAppApi?.setContentQualityMode(mode);
    }
    /** The slow-connection notice's button. */
    notifyRetryContentViewing() {
        this.streamrAppApi?.retryContentViewing('user').catch(console.error);
    }
    notifySetBroadcastingEnabled(enabled) {
        this.output.setBroadcastingEnabled(enabled);
        if (this.currentStreamId) {
            new UserStore().setBroadcastingEnabled(this.currentStreamId, enabled);
        }
        if (this.output.getSessionMode() === 'call') {
            this.streamrAppApi
                ?.setContentPublishingEnabled(enabled)
                .catch(console.error);
        }
        this.publishBroadcastMetadataNow();
    }
    notifySetAcceptedParticipantType(type) {
        UserService.getInstance().notifyAcceptedParticipantTypeChanged(type);
        if (this.currentStreamId) {
            new UserStore().setAcceptedParticipantType(this.currentStreamId, type);
        }
        this.streamrAppApi
            ?.setAcceptedParticipantType(type)
            .catch(console.error);
        this.publishBroadcastMetadataNow();
    }
    buildBroadcastMetadata() {
        return {
            viewerCount: 0,
            products: PurchaseService.getInstance().getProductsOfActiveBroadcast(),
            acceptedParticipantType: UserServiceOutput.getAcceptedParticipantType(),
            ...(this.lowQualityCompositeActive
                ? {
                    lowQualityPartition: LOW_CONTENT_STREAM_PART_PARTITION,
                    highQualityAvailable: this.highQualityAvailable,
                }
                : {}),
        };
    }
    publishBroadcastMetadataNow() {
        this.streamrAppApi?.publishBroadcastMetadata(this.buildBroadcastMetadata());
    }
    startBroadcastMetadataInterval() {
        this.stopBroadcastMetadataInterval();
        this.publishBroadcastMetadataNow();
        this.broadcastMetadataIntervalId = setInterval(() => {
            this.publishBroadcastMetadataNow();
        }, Settings.broadcastMetadataInterval);
    }
    async startSpeakerPublishing() {
        if (!this.streamrAppApi)
            return;
        await this.canvasReadyPromise;
        if (!this.streamrAppApi)
            return;
        await this.onMediaStreamChanged();
        await this.streamrAppApi
            .getMediaManager()
            .startPublishingViewerStream({ useDirectTracks: true });
        this.output.setIsVideoStreaming(true);
        const displayScene = SceneService.getInstance().getDisplayScene();
        this.buildAndPushDisplayConfig(displayScene);
    }
    stopBroadcastMetadataInterval() {
        if (this.broadcastMetadataIntervalId !== undefined) {
            clearInterval(this.broadcastMetadataIntervalId);
            this.broadcastMetadataIntervalId = undefined;
        }
    }
}
